import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { motion, AnimatePresence } from "framer-motion";
import ErrorPage from "../../Components/ErrorPage";
import { goToTop } from "../../Components/goToTop";
import { getAppCurrencyType } from "../../Utils/Common";
import { useAppDispatch } from "../../Controllers/hooks";

import {
    SOWDetailDataList,
    SOWSimpleDataList,
    QuotationTermData,
    Quotation
} from "../../Controllers/Types/Quotation/QuotationType";
import { QuotationNote } from "../../Controllers/Types/Quotation/QuotationNoteType";
import { QuotationTemplateCoverPage, QuotationTemplateItemList, QuotationTemplate as QuotationTemplateType } from "../../Controllers/Types/Quotation/QuotationTemplate";
import { updateQuotationProjectReferenceDetails } from "../../Api/Quotation/QuotationApiService";
import { fetchQuotationData } from "../../Controllers/Thunks/QuotationThunks";

const QuotationTemplate: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const currencySymbol = getAppCurrencyType();

    const [loading, setLoading] = useState<boolean>(false);
    const [showSideBar, setShowSideBar] = useState<boolean>(false);

    const template: QuotationTemplateType | null = location?.state?.template;
    const [error, setError] = useState<string>('');

    const [quotationDetailViewState, setQuotationDetailViewState] = useState<'Cover page' | 'Note' | 'Item' | 'Terms'>('Cover page');
    const globalQuotation: Quotation | null = location?.state?.quotation;
    const [coverPageData, setCoverPageData] = useState<QuotationTemplateCoverPage | null>(null);
    const [itemListData, setItemListData] = useState<QuotationTemplateItemList | null>(null);
    const [termData, setTermData] = useState<QuotationTermData | null>(null);
    const [noteListData, setNoteListData] = useState<QuotationNote[]>([]);

    const [itemViewOption, setItemViewOption] = useState<'Simple' | 'Detail'>('Simple');
    // Simple
    const [activeSimpleItem, setActiveSimpleItem] = useState<SOWSimpleDataList | null>(null);

    // Detail
    const [activeDetailItem, setActiveDetailItem] = useState<SOWDetailDataList | null>(null);


    useEffect(() => {
        if (template && template !== null) {
            setCoverPageData(template?.cover_page_data || null);
            setItemListData(template?.item_list || null);
            setTermData(template?.term_data || null);
            setNoteListData(template?.note_list || []);

            setError('');
        } else {
            setCoverPageData(null);
            setItemListData(null);
            setTermData(null);
            setNoteListData([]);

            setError('An error occurred while retrieving the quote template details.');
        }
    }, [template]);

    const handleChangeQuotationDetailViewState = (state: 'Cover page' | 'Note' | 'Item' | 'Terms') => {
        setQuotationDetailViewState(state);
        handleToggleSideBar();
    }

    const handleUseQuotationTemplate = () => {
        confirmDialog({
            message: 'Are you sure want to use this template?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-success',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: useQuotationTemplate,
        });
    }

    const useQuotationTemplate = async () => {
        const category = 'Template';
        const template_id = template?.id;

        const data = await updateQuotationProjectReferenceDetails(
            globalQuotation?.id!,
            category,
            template_id!
        );
        
        if (data.statusCode === 200) {
            await dispatch(fetchQuotationData());

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: `The quote was successfully created using the ${template?.quotation_template_name} template.`,
                    life: 3000
                });
            }

            // navigate
            navigate(`/quotation/list`, {
                state: {
                    quotation: globalQuotation
                }
            })
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'An error occurred while creating a quote using the template.',
                    life: 3000
                });
            }
        }
    }

    /* For item */
    const handleChangeItemViewOption = (state: 'Simple' | 'Detail') => {
        setItemViewOption(state);
    }

    useEffect(() => {
        if (itemListData && itemListData !== null) {
            if (itemViewOption === 'Simple') {
                setActiveSimpleItem(itemListData?.simple_data?.simple_data_list[0] || null);
            } else if (itemViewOption === 'Detail') {
                setActiveDetailItem(itemListData?.detail_data?.detail_data_list[0] || null);
            }
        }
    }, [itemViewOption, itemListData]);

    const amountLabelBody = (amount: number) => {
        return (
            <div className="item_count_lable custom text-end">
                <span>
                    {currencySymbol}&nbsp;{amount.toFixed(2)}
                </span>
            </div>
        )
    }

    const titleLabelBody = (title: string) => {
        return (
            <div className="item_count_lable custom text-end">
                <span>
                    {title}
                </span>
            </div>
        )
    }

    const countLabelBody = (count: number) => {
        return (
            <div className="item_count_lable custom text-end">
                <span>
                    {String(count).padStart(2, '0')}
                </span>
            </div>
        )
    }

    // Simple
    const handleToggleSimpleItem = (simpleItem: SOWSimpleDataList) => {
        setActiveSimpleItem((prevSelected) =>
            prevSelected?.id === simpleItem.id ? null : simpleItem
        );
        goToTop();
    };

    /* Detail */
    const handleToggleDetailItem = (detailItem: SOWDetailDataList) => {
        setActiveDetailItem((prevSelected) =>
            prevSelected?.id === detailItem.id ? null : detailItem
        );
        goToTop();
    };
    /*  */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };

    const handleToggleSideBar = () => {
        setShowSideBar(!showSideBar);
    }
    /* *************************************************************************** */


    return (
        <React.Fragment>
            <Toast ref={toast} />

            {error ? (
                <ErrorPage errorMessage={error} containerClassname="full_height" />
            ) : (
                <React.Fragment>
                    <div className="project_toolbar">
                        <Button
                            icon={`bi ${showSideBar ? 'bi-arrow-left' : 'bi-list'}`}
                            onClick={handleToggleSideBar} className="sidebar_toggle_btn"
                        />

                        <div className="project_tool_options">
                            <div className={`project_tool_options_sub w-100 justify-content-between`}>
                                <button className={`is_btn project_tool_btn ms-3 ms-sm-5 ms-lg-0 p-ripple`}
                                    onClick={handleUseQuotationTemplate}>
                                    Use this template
                                    <Ripple />
                                </button>

                                <button className={`is_btn project_tool_btn p-ripple`}
                                    onClick={goBack}>
                                    Back
                                    <Ripple />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={`project_sidebar ${showSideBar ? 'show' : ''}`}>
                        <ul className="section_list">
                            {/* Cover page */}
                            <li className="section_list_item mb-4">
                                <div className={`section_link_grp ${quotationDetailViewState === 'Cover page' ? 'active' : ''}`}>
                                    <i className="bi bi-caret-right-fill section_link_icon"></i>
                                    <button className="section_link is_btn"
                                        onClick={() => handleChangeQuotationDetailViewState('Cover page')}>
                                        <span>Cover page</span>
                                    </button>
                                </div>
                            </li>

                            {/* Note */}
                            <li className="section_list_item mb-4">
                                <div className={`section_link_grp ${quotationDetailViewState === 'Note' ? 'active' : ''}`}>
                                    <i className="bi bi-caret-right-fill section_link_icon"></i>
                                    <button className="section_link is_btn"
                                        onClick={() => handleChangeQuotationDetailViewState('Note')}>
                                        <span>Note</span>
                                    </button>
                                </div>
                            </li>

                            {/* SOW */}
                            <li className="section_list_item mb-4">
                                <div className={`section_link_grp ${quotationDetailViewState === 'Item' ? 'active' : ''}`}>
                                    <i className="bi bi-caret-right-fill section_link_icon"></i>
                                    <button className="section_link is_btn"
                                        onClick={() => handleChangeQuotationDetailViewState('Item')}>
                                        <span>SOW</span>
                                    </button>
                                </div>
                            </li>

                            {/* Terms */}
                            <li className="section_list_item">
                                <div className={`section_link_grp ${quotationDetailViewState === 'Terms' ? 'active' : ''}`}>
                                    <i className="bi bi-caret-right-fill section_link_icon"></i>
                                    <button className="section_link is_btn"
                                        onClick={() => handleChangeQuotationDetailViewState('Terms')}>
                                        <span>Terms</span>
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div className={`layout_backdrop ${showSideBar ? 'show' : ''}`} onClick={handleToggleSideBar}></div>

                    <div className={`project_main_content ${showSideBar ? 'has_sidebar' : ''}`}>
                        <div className="page_breadcrumb_area mb-3">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    Quotation
                                </li>

                                <li className="page_breadcrumb_item has_child">
                                    Quotation Template - {template?.quotation_template_name}
                                </li>

                                <li className="page_breadcrumb_item active">
                                    {quotationDetailViewState}
                                </li>
                            </ul>
                        </div>

                        {quotationDetailViewState === 'Cover page' ? (
                            /* Cover page */
                            <article className="main_section_area p-2 d-flex ">
                                {coverPageData && coverPageData !== null ? (
                                    <div className="cover_page_area">
                                        <img src={coverPageData?.comapany_logo || '/assets/images/icons/no_img.svg'} alt='' />

                                        {coverPageData?.project_description && (
                                            <p className="wide">{coverPageData?.project_description || '----'}</p>
                                        )}

                                        <h5><span>At</span></h5>
                                        <h5>{coverPageData?.project_address || '----'}</h5>
                                        <br />

                                        <h5><span>For</span></h5>
                                        <h5>
                                            {coverPageData?.client_name || '---'}
                                        </h5>
                                    </div>
                                ) : (
                                    <div className="no_content_added_area">
                                        <img src="/assets/images/no_data/no_data_img.png" alt="No data!" />
                                        <p>No cover page found!</p>
                                    </div>
                                )}
                            </article>
                        ) : quotationDetailViewState === 'Note' ? (
                            /* Note */
                            <article className="main_section_area p-2">
                                {noteListData && noteListData?.length > 0 ? (
                                    <div className="w-100 p-sm-1">
                                        {noteListData?.map((note, index) => {
                                            return (
                                                <article key={note?.id} className="note_content_container">
                                                    <div className="note_content_area">
                                                        <p>{String(index + 1).padStart(2, '0')}.&nbsp;</p>
                                                        <p>
                                                            {note?.note}
                                                        </p>
                                                    </div>
                                                </article>
                                            )
                                        })}
                                    </div>
                                ) : (
                                    <div className="no_content_added_area">
                                        <img src="/assets/images/no_data/no_data_img.png" alt="No data!" />
                                        <p>No note(s) found!</p>
                                    </div>
                                )}
                            </article>
                        ) : quotationDetailViewState === 'Item' ? (
                            /* Item */
                            <React.Fragment>
                                {itemListData && itemListData !== null ? (
                                    <React.Fragment>
                                        <div className="page_header_area mb-3">
                                            <div className="contact_tabs">
                                                <div className="contact_tab_item">
                                                    <button
                                                        type="button"
                                                        className={`contact_tab_btn fixed_min_width ${itemViewOption === "Simple" ? "active" : ""} p-ripple`}
                                                        onClick={() => handleChangeItemViewOption('Simple')}
                                                    >
                                                        Simple
                                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                                    </button>
                                                </div>
                                                <div className="contact_tab_item">
                                                    <button
                                                        type="button"
                                                        className={`contact_tab_btn fixed_min_width ${itemViewOption === "Detail" ? "active" : ""} p-ripple`}
                                                        onClick={() => handleChangeItemViewOption('Detail')}
                                                    >
                                                        Detail
                                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="page_action_area">
                                                <div className="display_label">
                                                    <span className="display_label_text">Total :&nbsp;</span>

                                                    {itemViewOption === 'Simple' ? (
                                                        <span>
                                                            {currencySymbol}&nbsp;
                                                            {(itemListData?.simple_data?.simple_data_total ?? 0)?.toLocaleString("en-GB", {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            })}
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            {currencySymbol}&nbsp;
                                                            {(itemListData?.detail_data?.detail_data_total ?? 0)?.toLocaleString("en-GB", {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            })}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <article className="main_section_area p-2">
                                            {itemViewOption === 'Simple' && (
                                                <div className="pck_track_list_area">
                                                    <div className="table-responsive pck_track_list_table_area">
                                                        <table className="table pck_track_list_table">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: "4%" }} className="text_no_wrap toggle_column"></th>

                                                                    <th style={{ width: "30%" }} className="text_no_wrap item_title_column">
                                                                        Item
                                                                    </th>

                                                                    <th style={{ width: "22%" }} className="text-center item_value_column text_no_wrap">
                                                                        Material
                                                                    </th>

                                                                    <th style={{ width: "22%" }} className="text-center item_value_column text_no_wrap">
                                                                        Labour
                                                                    </th>

                                                                    <th style={{ width: "22%" }} className="text-center item_value_column text_no_wrap">
                                                                        Total
                                                                    </th>
                                                                </tr>
                                                            </thead>

                                                            <tbody>
                                                                {itemListData?.simple_data?.simple_data_list && itemListData?.simple_data?.simple_data_list?.length > 0 && (
                                                                    <React.Fragment>
                                                                        {itemListData?.simple_data?.simple_data_list?.map((simpleData) => {
                                                                            const isActive = simpleData?.id === activeSimpleItem?.id;

                                                                            return (
                                                                                <React.Fragment>
                                                                                    <tr
                                                                                        key={`simpleItem_${simpleData?.id}`}
                                                                                        onClick={() => handleToggleSimpleItem(simpleData)}
                                                                                        className={`part_row ${isActive ? "active" : ""}`}
                                                                                    >
                                                                                        <td
                                                                                            style={{ width: "4rem" }}
                                                                                            className="text-center toggle_column"
                                                                                        >
                                                                                            <i className={`bi bi-chevron-right ${isActive ? "toggled" : ""} data_toggle_icon`}></i>
                                                                                        </td>

                                                                                        <td
                                                                                            style={{ width: "74%" }}
                                                                                            colSpan={3}
                                                                                            className="text_no_wrap text_semi_bold"
                                                                                        >
                                                                                            {simpleData?.quotation_item_name}
                                                                                        </td>

                                                                                        <td
                                                                                            style={{ width: "22%" }}
                                                                                            className="text_no_wrap item_value_column"
                                                                                        >
                                                                                            {amountLabelBody(simpleData?.item_total || 0)}
                                                                                        </td>
                                                                                    </tr>

                                                                                    <AnimatePresence initial={false}>
                                                                                        {isActive && (
                                                                                            <tr className="part_item_row">
                                                                                                <td
                                                                                                    colSpan={5}
                                                                                                    className="p-0"
                                                                                                    style={{ width: "100%" }}
                                                                                                >
                                                                                                    <motion.div
                                                                                                        initial={{ height: 0, opacity: 0 }}
                                                                                                        animate={{ height: "auto", opacity: 1 }}
                                                                                                        exit={{ height: 0, opacity: 0 }}
                                                                                                        transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1], }}
                                                                                                        style={{ overflow: "hidden" }}
                                                                                                    >
                                                                                                        {simpleData?.sub_item_list && simpleData?.sub_item_list?.length > 0 ? (
                                                                                                            <table className="table pck_track_list_table">
                                                                                                                {simpleData?.sub_item_list?.map((simpleSubItem) => {
                                                                                                                    return (
                                                                                                                        <tr key={`simpleSubItem${simpleSubItem?.id}`} className="part_item_sub_row view_only">
                                                                                                                            <td style={{ width: "4rem" }} className="toggle_column"></td>
                                                                                                                            <td
                                                                                                                                style={{ width: "30%" }}
                                                                                                                                className="text_semi_bold item_title_column"
                                                                                                                            >
                                                                                                                                <span className="highlight">
                                                                                                                                    {simpleSubItem?.quotation_sub_item_name ? simpleSubItem?.quotation_sub_item_name : '---'}
                                                                                                                                </span>
                                                                                                                            </td>

                                                                                                                            <td
                                                                                                                                style={{ width: "22%" }}
                                                                                                                                className="text_no_wrap item_value_column"
                                                                                                                            >
                                                                                                                                {amountLabelBody(simpleSubItem?.sub_item_simple_data?.material || 0)}
                                                                                                                            </td>

                                                                                                                            <td
                                                                                                                                style={{ width: "22%" }}
                                                                                                                                className="text_no_wrap item_value_column"
                                                                                                                            >
                                                                                                                                {amountLabelBody(simpleSubItem?.sub_item_simple_data?.labour || 0)}
                                                                                                                            </td>

                                                                                                                            <td
                                                                                                                                style={{ width: "22%" }}
                                                                                                                                className="text_no_wrap item_value_column"
                                                                                                                            >
                                                                                                                                {amountLabelBody(simpleSubItem?.final_total || 0)}
                                                                                                                            </td>
                                                                                                                        </tr>
                                                                                                                    )
                                                                                                                })}
                                                                                                            </table>
                                                                                                        ) : (
                                                                                                            <table className="table pck_track_list_table">
                                                                                                                <tr className="part_item_sub_row">
                                                                                                                    <td className="text-center empty_data_column" colSpan={6}>
                                                                                                                        <i className="bi bi-exclamation-circle"></i>
                                                                                                                        No items found.
                                                                                                                    </td>
                                                                                                                </tr>
                                                                                                            </table>
                                                                                                        )}
                                                                                                    </motion.div>
                                                                                                </td>
                                                                                            </tr>
                                                                                        )}
                                                                                    </AnimatePresence>
                                                                                </React.Fragment>
                                                                            )
                                                                        })}
                                                                    </React.Fragment>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {itemViewOption === 'Detail' && (
                                                <div className="pck_track_list_area">
                                                    <div className="table-responsive pck_track_list_table_area">
                                                        <table className="table pck_track_list_table">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: "4%" }} className="text_no_wrap toggle_column"></th>

                                                                    <th style={{ width: "20%" }} className="text_no_wrap item_title_column">
                                                                        Item
                                                                    </th>

                                                                    <th style={{ width: "8%" }} className="text-center item_sub_value_column text_no_wrap">
                                                                        Unit
                                                                    </th>

                                                                    <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                                                        Price
                                                                    </th>

                                                                    <th style={{ width: "8%" }} className="text-center item_sub_value_column text_no_wrap">
                                                                        QTY.
                                                                    </th>

                                                                    <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                                                        Material
                                                                    </th>

                                                                    <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                                                        Labour
                                                                    </th>

                                                                    <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                                                        Total
                                                                    </th>
                                                                </tr>
                                                            </thead>

                                                            <tbody>
                                                                {itemListData?.detail_data?.detail_data_list && itemListData?.detail_data?.detail_data_list?.length > 0 && (
                                                                    <React.Fragment>
                                                                        {itemListData?.detail_data?.detail_data_list?.map((detailData) => {
                                                                            const isActive = detailData?.id === activeDetailItem?.id;

                                                                            return (
                                                                                <React.Fragment>
                                                                                    <tr
                                                                                        key={`detailItem_${detailData?.id}`}
                                                                                        onClick={() => handleToggleDetailItem(detailData)}
                                                                                        className={`part_row ${isActive ? "active" : ""}`}
                                                                                    >
                                                                                        <td
                                                                                            style={{ width: "4rem" }}
                                                                                            className="text-center toggle_column"
                                                                                        >
                                                                                            <i className={`bi bi-chevron-right ${isActive ? "toggled" : ""} data_toggle_icon`}></i>
                                                                                        </td>

                                                                                        <td
                                                                                            style={{ width: "81%" }}
                                                                                            colSpan={6}
                                                                                            className="text_no_wrap text_semi_bold"
                                                                                        >
                                                                                            {detailData?.quotation_item_name}
                                                                                        </td>

                                                                                        <td
                                                                                            style={{ width: "15%" }}
                                                                                            className="text_no_wrap item_value_column"
                                                                                        >
                                                                                            {amountLabelBody(detailData?.item_total || 0)}
                                                                                        </td>
                                                                                    </tr>

                                                                                    <AnimatePresence initial={false}>
                                                                                        {isActive && (
                                                                                            <tr className="part_item_row">
                                                                                                <td
                                                                                                    colSpan={8}
                                                                                                    className="p-0"
                                                                                                    style={{ width: "100%" }}
                                                                                                >
                                                                                                    <motion.div
                                                                                                        initial={{ height: 0, opacity: 0 }}
                                                                                                        animate={{ height: "auto", opacity: 1 }}
                                                                                                        exit={{ height: 0, opacity: 0 }}
                                                                                                        transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1], }}
                                                                                                        style={{ overflow: "hidden" }}
                                                                                                    >
                                                                                                        {detailData?.sub_item_list && detailData?.sub_item_list?.length > 0 ? (
                                                                                                            <table className="table pck_track_list_table">
                                                                                                                {detailData?.sub_item_list?.map((detailSubItem) => {
                                                                                                                    return (
                                                                                                                        <tr key={`detailSubItem${detailSubItem?.id}`} className="part_item_sub_row view_only">
                                                                                                                            <td style={{ width: "4rem" }} className="toggle_column"></td>
                                                                                                                            <td
                                                                                                                                style={{ width: "20%" }}
                                                                                                                                className="text_semi_bold item_title_column"
                                                                                                                            >
                                                                                                                                <span className="highlight">
                                                                                                                                    {detailSubItem?.quotation_sub_item_name ? detailSubItem?.quotation_sub_item_name : '---'}
                                                                                                                                </span>
                                                                                                                            </td>

                                                                                                                            <td
                                                                                                                                style={{ width: "8%" }}
                                                                                                                                className="text_no_wrap item_sub_value_column"
                                                                                                                            >
                                                                                                                                {titleLabelBody(detailSubItem?.sub_item_detail_data?.unit?.unit_name || '---')}
                                                                                                                            </td>

                                                                                                                            <td
                                                                                                                                style={{ width: "15%" }}
                                                                                                                                className="text_no_wrap item_value_column"
                                                                                                                            >
                                                                                                                                {amountLabelBody(detailSubItem?.sub_item_detail_data?.unit_price || 0)}
                                                                                                                            </td>

                                                                                                                            <td
                                                                                                                                style={{ width: "8%" }}
                                                                                                                                className="text_no_wrap item_sub_value_column"
                                                                                                                            >
                                                                                                                                {countLabelBody(detailSubItem?.sub_item_detail_data?.quantity || 0)}
                                                                                                                            </td>

                                                                                                                            <td
                                                                                                                                style={{ width: "15%" }}
                                                                                                                                className="text_no_wrap item_value_column"
                                                                                                                            >
                                                                                                                                {amountLabelBody(detailSubItem?.sub_item_detail_data?.material_cost || 0)}
                                                                                                                            </td>

                                                                                                                            <td
                                                                                                                                style={{ width: "15%" }}
                                                                                                                                className="text_no_wrap item_value_column"
                                                                                                                            >
                                                                                                                                {amountLabelBody(detailSubItem?.sub_item_detail_data?.labour_cost || 0)}
                                                                                                                            </td>

                                                                                                                            <td
                                                                                                                                style={{ width: "15%" }}
                                                                                                                                className="text_no_wrap item_value_column"
                                                                                                                            >
                                                                                                                                {amountLabelBody(detailSubItem?.final_total || 0)}
                                                                                                                            </td>
                                                                                                                        </tr>
                                                                                                                    )
                                                                                                                })}
                                                                                                            </table>
                                                                                                        ) : (
                                                                                                            <table className="table pck_track_list_table">
                                                                                                                <tr className="part_item_sub_row">
                                                                                                                    <td className="text-center empty_data_column" colSpan={9}>
                                                                                                                        <i className="bi bi-exclamation-circle"></i>
                                                                                                                        No items found.
                                                                                                                    </td>
                                                                                                                </tr>
                                                                                                            </table>
                                                                                                        )}
                                                                                                    </motion.div>
                                                                                                </td>
                                                                                            </tr>
                                                                                        )}
                                                                                    </AnimatePresence>
                                                                                </React.Fragment>
                                                                            )
                                                                        })}
                                                                    </React.Fragment>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </article>
                                    </React.Fragment>
                                ) : (
                                    <article className="main_section_area p-2">
                                        <div className="no_content_added_area">
                                            <img src="/assets/images/no_data/no_data_img.png" alt="No data!" />
                                            <p>No item data found!</p>
                                        </div>
                                    </article>
                                )}
                            </React.Fragment>
                        ) : quotationDetailViewState === 'Terms' ? (
                            /* Terms */
                            <article className="main_section_area p-2">
                                {termData && termData !== null ? (
                                    <div className="input_label_flex_area">
                                        {/* Advance payment */}
                                        <div className="input_label_flex_grp">
                                            <div className="input_label_flex_sub">
                                                <h6 className="input_label">Advance payment:</h6>

                                                <div className="display_input muted">
                                                    {termData?.advance_payment ? (
                                                        <span>
                                                            {currencySymbol}&nbsp;{(termData?.advance_payment).toFixed(2)}
                                                        </span>
                                                    ) : (
                                                        <span className="text_placeholder">
                                                            {currencySymbol}&nbsp;0.00
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Start date */}
                                        <div className="input_label_flex_grp">
                                            <div className="input_label_flex_sub">
                                                <h6 className="input_label">Start date:</h6>

                                                <div className="display_input muted">
                                                    {termData?.start_date ? (
                                                        <span>
                                                            {termData?.start_date}
                                                        </span>
                                                    ) : (
                                                        <span className="text_placeholder">
                                                            dd/mm/yyyy
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Duration */}
                                        <div className="input_label_flex_grp">
                                            <div className="input_label_flex_sub">
                                                <h6 className="input_label">Duration:</h6>

                                                <div className="display_input muted">
                                                    {(termData.duration_type && termData?.duration) ? (
                                                        <span>
                                                            {termData?.duration_type[0]}&nbsp;&nbsp;
                                                            {String(termData?.duration).padStart(2, '0')}
                                                        </span>
                                                    ) : (
                                                        <span className="text_placeholder">
                                                            W&nbsp;&nbsp;00
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="no_content_added_area">
                                        <img src="/assets/images/no_data/no_data_img.png" alt="No data!" />
                                        <p>No term data found!</p>
                                    </div>
                                )}
                            </article>
                        ) : null}
                    </div>
                </React.Fragment>
            )}
        </React.Fragment>
    )
}

export default QuotationTemplate